/**
 * Partner-screen reads that limits written in the code used to cut. They were wrong at any Max rows
 * value, and each is now complete or throws.
 */

import { supabase } from '@/integrations/supabase/client';
import { assertComplete, fetchAllRows } from '@/lib/fetchAllRows';
import { businessDay } from '@/lib/format';

// ─── Summary tab: default start date ─────────────────────────────────────────

/**
 * The partner's earliest signed agreement, as a Ghana calendar day, or null when there is none.
 *
 * ONE ORDERED ROW, NOT AN UNORDERED 100. The Summary tab opens on the month of this date, and every
 * stat card's default window starts there. It used to read `.limit(100)` of the partner's vehicles
 * with no order and take the earliest agreement among them. Primelane has 259 vehicles and 3 of
 * them carry its March 2020 agreements, so the right date arrived only when one of those 3 happened
 * to be in the slice. This asks the database for the minimum directly: the earliest `signed_at` of
 * any agreement on any contract of any of the partner's vehicles, the same set the loop walked.
 * A failed read still leaves the default range in place, as before.
 */
export async function fetchPartnerEarliestSignedAt(partnerId: number): Promise<Date | null> {
    const { data, error } = await supabase
        .from('contract_agreements')
        .select('signed_at, contracts!inner(vehicles!inner(partner_id))')
        .eq('contracts.vehicles.partner_id', partnerId)
        .not('signed_at', 'is', null)
        .order('signed_at', { ascending: true })
        .limit(1);
    if (error) return null;
    return businessDay(data?.[0]?.signed_at ?? null);
}

// ─── Summary tab: Weekly Earnings chart ──────────────────────────────────────

export interface WeeklyEarningsRow {
    group_key: string;
    gross_repayments: number;
    partner_portion: number;
    total_groups: number;
}

const WEEKS_SUBJECT = 'weeks of partner earnings';

/**
 * Every week of earnings in the range, oldest first.
 *
 * EVERY WEEK, NOT THE NEWEST 100. The chart asked for `p_page_size: 100`, and Primelane has 344
 * repayment weeks since March 2020, so the chart drew Nov 2024 onward (GHS 9.63M of 13.06M gross)
 * beside stat cards that covered the whole range. It now omits `p_page`/`p_page_size`, which makes
 * the function's `LIMIT COALESCE(p_page_size, 1000000)` its no-paging mode, and pages the result
 * with PostgREST on `group_key`. A week grouping's key is the ISO week ('IYYY-IW'), unique per row
 * and in date order as text, so the rows come back oldest first and the chart no longer reverses
 * them. The function's own `total_groups` count is checked as well as PostgREST's.
 */
export async function fetchPartnerWeeklyEarnings(
    partnerId: number,
    fromInstant: string,
    toInstant: string,
): Promise<WeeklyEarningsRow[]> {
    const rows = await fetchAllRows<WeeklyEarningsRow>({
        label: WEEKS_SUBJECT,
        orderedBy: 'group_key',
        page: (from, to) =>
            supabase
                .rpc(
                    'get_partner_earnings_explorer',
                    {
                        p_partner_id: partnerId,
                        p_from_date: fromInstant,
                        p_to_date: toInstant,
                        p_group_by: 'week',
                    },
                    { count: 'exact' },
                )
                .order('group_key', { ascending: true })
                .range(from, to),
        keyOf: (week) => week.group_key,
    });
    const counted = rows.reduce((most, r) => Math.max(most, r.total_groups ?? 0), 0);
    assertComplete(rows, counted, WEEKS_SUBJECT, 'the earnings RPC returned fewer rows than it counted');
    return rows;
}

// ─── Manage Partner: Assign / Unassign Vehicles ──────────────────────────────

export interface SheetVehicle {
    id: number;
    make: string;
    model: string;
    color: string;
    vin: string;
    registration_number: string | null;
}

/** The Unassign sheet lists one partner's vehicles; the Assign sheet lists vehicles with no partner. */
export type SheetScope = { partnerId: number } | { unassigned: true };

/** How many vehicles a sheet lists at once. A search reaches every vehicle in scope, not these. */
const SHEET_LIMIT = 50;

/**
 * A value for a PostgREST `or()` term, double-quoted so a comma, dot, colon or bracket typed into
 * the search cannot end the term early. Inside the quotes a backslash escapes `"` and `\`.
 */
function orValue(value: string): string {
    return `"${value.replace(/[\\"]/g, '\\$&')}"`;
}

/**
 * The sheet's vehicles: with no search, the newest 50 in scope; with a search, the newest 50 that
 * match it, found by the server across every vehicle in scope.
 *
 * SEARCH BEFORE THE LIMIT. Both sheets read the newest 50 vehicles and then searched in the browser
 * over those 50. Primelane has 259, so its 209 oldest could be neither listed nor found, and could
 * not be unassigned from this sheet. The search now runs on the server, over make, model, colour,
 * VIN and registration, before the limit, as `AssignVehicleTransactionSheet` already does. The
 * registration lives on another table, so its matches are found first and folded in by id.
 */
export async function fetchPartnerSheetVehicles(scope: SheetScope, search: string): Promise<SheetVehicle[]> {
    let registrationIds: number[] = [];
    if (search) {
        const matches = await fetchAllRows<{ id: number }>({
            label: 'vehicles whose registration matches the search',
            orderedBy: 'id',
            page: (from, to) => {
                const q = supabase
                    .from('vehicles')
                    .select('id, vehicle_registrations!inner(number)', { count: 'exact' })
                    .ilike('vehicle_registrations.number', `%${search}%`);
                return ('partnerId' in scope ? q.eq('partner_id', scope.partnerId) : q.is('partner_id', null))
                    .order('id', { ascending: true })
                    .range(from, to);
            },
            keyOf: (vehicle) => vehicle.id,
        });
        registrationIds = matches.map((m) => m.id);
    }

    let query = supabase
        .from('vehicles')
        .select('id, make, model, color, vin, vehicle_registrations(number)');
    query = 'partnerId' in scope ? query.eq('partner_id', scope.partnerId) : query.is('partner_id', null);
    if (search) {
        const pattern = orValue(`%${search}%`);
        const terms = ['make', 'model', 'color', 'vin'].map((column) => `${column}.ilike.${pattern}`);
        if (registrationIds.length > 0) terms.push(`id.in.(${registrationIds.join(',')})`);
        query = query.or(terms.join(','));
    }
    const { data, error } = await query
        .order('created_at', { ascending: false })
        .order('id', { ascending: false })
        .limit(SHEET_LIMIT);
    if (error) throw error;

    return (data ?? []).map((v) => ({
        id: v.id,
        make: v.make,
        model: v.model,
        color: v.color,
        vin: v.vin,
        registration_number: v.vehicle_registrations?.number || null,
    }));
}
